import React, { forwardRef, createContext, useContext } from 'react'
import { RadioGroup as ArkRadioGroup, type RadioGroupRootProps, type RadioGroupItemProps } from '@ark-ui/react'
import { classNames as cn } from '~/helpers/string'

export type RadioSize = 'sm' | 'md'

export interface RadioGroupProps extends RadioGroupRootProps {
  size?: RadioSize
}

export interface RadioProps extends RadioGroupItemProps {
  rootRef?: React.Ref<HTMLLabelElement>
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  size?: RadioSize
}

const RadioSizeContext = createContext<RadioSize>('md')

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(function (props, ref) {
  const { className = '', size = 'md', ...rest } = props
  return (
    <RadioSizeContext.Provider value={size}>
      <ArkRadioGroup.Root ref={ref} className={cn('flex flex-col gap-2.5', className)} {...rest} />
    </RadioSizeContext.Provider>
  )
})

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function (props, ref) {
  const { children, rootRef, inputProps, className = '', size, ...rest } = props
  const contextSize = size ?? useContext(RadioSizeContext)
  return (
    <ArkRadioGroup.Item
      ref={rootRef}
      className={cn('flex w-fit flex-row items-center justify-start gap-2 rtl:justify-end', className)}
      {...rest}
    >
      <ArkRadioGroup.ItemHiddenInput ref={ref} {...inputProps} />
      <ArkRadioGroup.ItemControl
        className={cn(
          'relative rounded-full bg-white ring-brand-gray-700 data-[state=checked]:bg-brand-primary-500 data-[state=checked]:data-[disabled]:bg-brand-gray-400 data-[disabled]:ring-brand-gray-400 data-[focus]:ring-brand-primary-500 data-[hover]:ring-brand-primary-500 data-[state=checked]:data-[disabled]:ring-brand-gray-400 data-[state=checked]:ring-brand-primary-500',
          contextSize === 'sm'
            ? 'm-[3.5px] h-1.5 w-1.5 ring-[0.5px] ring-offset-[3.5px]'
            : 'm-1 h-2 w-2 ring-1 ring-offset-4',
        )}
      />
      {children && (
        <ArkRadioGroup.ItemText
          className={cn(
            'font-normal text-brand-gray-700 data-[disabled]:text-brand-gray-400',
            contextSize === 'sm' ? 'text-sm' : 'text-base',
          )}
        >
          {children}
        </ArkRadioGroup.ItemText>
      )}
    </ArkRadioGroup.Item>
  )
})
